import React, { useState, useEffect, useRef, TouchEvent } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';

interface ImagePreviewProps {
  imageUrl: string;
  isOpen: boolean;
  onClose: () => void;
  alt?: string;
  images?: string[];
  currentIndex?: number;
  onNavigate?: (index: number) => void;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ 
  imageUrl, 
  isOpen, 
  onClose, 
  alt = '预览图片',
  images = [],
  currentIndex = 0,
  onNavigate
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 触摸相关状态
  const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });
  const [touchStartScale, setTouchStartScale] = useState(1);
  const [touchStartDistance, setTouchStartDistance] = useState(0);
  const [isTouching, setIsTouching] = useState(false);
  
  // 动画相关状态
  const motionScale = useMotionValue(1);
  const opacity = useTransform(motionScale, [0.8, 1], [0, 1]);
  
  // 多图导航状态
  const hasMultipleImages = images.length > 1;
  
  // 重置缩放比例和位置
  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      motionScale.set(1);
    }
  }, [isOpen, imageUrl, motionScale]);
  
  // 处理鼠标滚轮缩放
  const handleWheel = (e: React.WheelEvent) => {
    // 阻止默认行为和事件冒泡
    e.preventDefault();
    e.stopPropagation();
    
    // 确保事件不会传播到父元素
    if (e.cancelable) {
      e.nativeEvent.stopImmediatePropagation();
    }
    
    const delta = e.deltaY * -0.01;
    const newScale = Math.min(Math.max(0.5, scale + delta), 5); // 限制缩放范围在0.5-5倍之间
    setScale(newScale);
    motionScale.set(newScale);
  };
  
  // 处理点击关闭
  const handleClick = (e: React.MouseEvent) => {
    // 确保点击的是背景或图片，而不是其他控件
    if (e.target === e.currentTarget || e.target === imageRef.current) {
      onClose();
    }
  };
  
  // 处理键盘事件
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isOpen) {
        if (e.key === 'Escape') {
          onClose();
        } else if (e.key === '+' || e.key === '=') {
          const newScale = Math.min(scale + 0.2, 5);
          setScale(newScale);
          motionScale.set(newScale);
        } else if (e.key === '-') {
          const newScale = Math.max(scale - 0.2, 0.5);
          setScale(newScale);
          motionScale.set(newScale);
        } else if (e.key === '0') {
          setScale(1);
          setPosition({ x: 0, y: 0 });
          motionScale.set(1);
        } else if (e.key === 'ArrowLeft') {
          if (hasMultipleImages && scale === 1) {
            // 当缩放为1时，左右箭头用于导航图片
            navigateToPrevious();
          } else {
            // 缩放状态下，左右箭头用于移动图片
            setPosition(prev => ({ ...prev, x: prev.x + 20 }));
          }
        } else if (e.key === 'ArrowRight') {
          if (hasMultipleImages && scale === 1) {
            // 当缩放为1时，左右箭头用于导航图片
            navigateToNext();
          } else {
            // 缩放状态下，左右箭头用于移动图片
            setPosition(prev => ({ ...prev, x: prev.x - 20 }));
          }
        } else if (e.key === 'ArrowUp') {
          setPosition(prev => ({ ...prev, y: prev.y + 20 }));
        } else if (e.key === 'ArrowDown') {
          setPosition(prev => ({ ...prev, y: prev.y - 20 }));
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, scale, motionScale, hasMultipleImages, currentIndex]);
  
  // 处理触摸开始
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setIsTouching(true);
    
    if (e.touches.length === 1) {
      // 单指触摸 - 准备移动
      setTouchStartPos({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    } else if (e.touches.length === 2) {
      // 双指触摸 - 准备缩放
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      setTouchStartDistance(distance);
      setTouchStartScale(scale);
    }
  };
  
  // 处理触摸移动
  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    e.preventDefault(); // 防止页面滚动
    
    if (e.touches.length === 1) {
      // 单指触摸 - 移动图片
      const newPosition = {
        x: e.touches[0].clientX - touchStartPos.x,
        y: e.touches[0].clientY - touchStartPos.y
      };
      setPosition(newPosition);
    } else if (e.touches.length === 2) {
      // 双指触摸 - 缩放图片
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // 计算新的缩放比例
      const newScale = touchStartScale * (distance / touchStartDistance);
      const clampedScale = Math.min(Math.max(0.5, newScale), 5);
      setScale(clampedScale);
      motionScale.set(clampedScale);
    }
  };
  
  // 处理触摸结束
  const handleTouchEnd = () => {
    setIsTouching(false);
  };
  
  // 双击重置缩放
  const handleDoubleClick = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    motionScale.set(1);
  };
  
  // 导航到下一张图片
  const navigateToNext = () => {
    if (hasMultipleImages && onNavigate) {
      const nextIndex = (currentIndex + 1) % images.length;
      onNavigate(nextIndex);
      // 重置缩放和位置
      setScale(1);
      setPosition({ x: 0, y: 0 });
      motionScale.set(1);
    }
  };
  
  // 导航到上一张图片
  const navigateToPrevious = () => {
    if (hasMultipleImages && onNavigate) {
      const prevIndex = (currentIndex - 1 + images.length) % images.length;
      onNavigate(prevIndex);
      // 重置缩放和位置
      setScale(1);
      setPosition({ x: 0, y: 0 });
      motionScale.set(1);
    }
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={containerRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm"
          onClick={handleClick}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ overscrollBehavior: 'none' }} // 防止滚动传播
        >
          <div className="relative w-full h-full flex items-center justify-center p-4">
            <motion.img
              ref={imageRef}
              src={hasMultipleImages && images.length > 0 ? images[currentIndex] : imageUrl}
              alt={alt}
              className="max-w-full max-h-full object-contain"
              style={{ 
                transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
                transition: isTouching ? 'none' : 'transform 0.1s ease-out',
                transformOrigin: 'center center'
              }}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onDoubleClick={handleDoubleClick}
            />
            
            {/* 多图导航按钮 */}
            {hasMultipleImages && (
              <>
                {/* 上一张按钮 */}
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToPrevious();
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-dark-900 bg-opacity-70 text-white hover:bg-dark-700 active:bg-dark-800 transition-colors"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -20, opacity: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  ←
                </motion.button>
                
                {/* 下一张按钮 */}
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToNext();
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-dark-900 bg-opacity-70 text-white hover:bg-dark-700 active:bg-dark-800 transition-colors"
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 20, opacity: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  →
                </motion.button>
                
                {/* 图片计数指示器 */}
                <motion.div
                  className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-dark-900 bg-opacity-70 px-3 py-1 rounded-full text-white text-sm"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 20, opacity: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  {currentIndex + 1} / {images.length}
                </motion.div>
              </>
            )}
            
            {/* 关闭按钮 */}
            <motion.button 
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-dark-900 bg-opacity-70 text-white hover:bg-dark-700 active:bg-dark-800 transition-colors"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ delay: 0.1 }}
            >
              ✕
            </motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImagePreview;
